import type { GetClaimsParams } from '@/client/claims/get-claims'
import type { GetClaimsStatsParams } from '@/client/claims/get-claims-stats'
import type { GetAssignedUsersParams } from '@/client/claims/get-assigned-users'
import type { TGetStageClaimsApiParams } from '@/client/claims/get-stage-claims'
import type { TStageKey } from '@/app/(internal-new)/claims/stage-v2/[stage]/stage.utils'

export const claimsKeys = {
  all: ['claims'] as const,
  lists: () => [...claimsKeys.all, 'list'] as const,
  list: (params: GetClaimsParams) => [...claimsKeys.lists(), params] as const,
  infinite: (params: GetClaimsParams) => [...claimsKeys.lists(), 'infinite', params] as const,
  stats: (params: GetClaimsStatsParams) => [...claimsKeys.all, 'stats', params] as const,
  details: () => [...claimsKeys.all, 'detail'] as const,
  detail: (id: string) => [...claimsKeys.details(), id] as const,
}

/**
 * Cache keys do endpoint `/api/claims/stage/[stage]`.
 *
 * Vive ao lado de `claimsKeys` (mesmo arquivo) pra manter o factory como
 * single-source-of-truth do cache de claims — splitar em outro file
 * espalharia patterns de invalidação (ex: realtime invalidar TANTO
 * `claimsKeys.all` quanto `stageClaimsKeys.all` num único subscriber).
 *
 * Hierarquia idêntica ao `claimsKeys` pra invalidação granular:
 * - `stageClaimsKeys.all` invalida tudo de stage
 * - `stageClaimsKeys.list(stage)` invalida só um estágio (ex: pós-advance)
 * - `stageClaimsKeys.infinite(stage, params)` invalida um snapshot específico
 */
export const stageClaimsKeys = {
  all: ['stage-claims'] as const,
  list: (stage: TStageKey) => [...stageClaimsKeys.all, 'list', stage] as const,
  infinite: (stage: TStageKey, params: TGetStageClaimsApiParams) =>
    [...stageClaimsKeys.list(stage), 'infinite', params] as const,
  /**
   * Reservado pro count separado de "Possíveis Recusas". Endpoint dedicado
   * ainda não existe (followup); o hook hoje retorna `null`.
   */
  refusedCount: (stage: TStageKey) => [...stageClaimsKeys.all, 'refused-count', stage] as const,
}

export const productsKeys = {
  all: ['products'] as const,
  list: () => [...productsKeys.all, 'list'] as const,
}

export const coverageTypesKeys = {
  all: ['coverage-types'] as const,
  list: () => [...coverageTypesKeys.all, 'list'] as const,
}

export const assignedUsersKeys = {
  all: ['assigned-users'] as const,
  list: (params?: GetAssignedUsersParams) =>
    [...assignedUsersKeys.all, 'list', params ?? {}] as const,
}

export const observationCategoriesKeys = {
  all: ['observation-categories'] as const,
  list: () => [...observationCategoriesKeys.all, 'list'] as const,
}

/**
 * Query keys factory pra rota `/claims/[id]` (consumer wrapper sobre o
 * ClaimDetailPage Container do DS).
 *
 * `all` permite invalidar a tela inteira após realtime event sem precisar
 * iterar key-a-key.
 *
 * `affectedByClaimChange` agrega TODAS as factories que precisam invalidar
 * quando UMA mutation do detalhe muda dados visíveis em outras telas (lista
 * de claims, stats, etc). Use este em `invalidateQueries` em vez de misturar
 * factory + hardcoded `['claims']`, que é fonte de drift silencioso.
 */
/**
 * Query keys factory pra rota `/laudo` (novo laudo fotográfico com DS).
 *
 * `job(jobId)` é a chave de polling — invalidar quando job mudar de status.
 * `photos(draftId)` lista as fotos+legendas de um draft.
 * `activeJob` é lookup de reconexão ao montar.
 */
export const photoCaptionsKeys = {
  all: ['photo-captions'] as const,
  activeJob: () => [...photoCaptionsKeys.all, 'active-job'] as const,
  job: (jobId: string) => [...photoCaptionsKeys.all, 'job', jobId] as const,
  photos: (draftId: string) => [...photoCaptionsKeys.all, 'photos', draftId] as const,
  recentDraft: () => [...photoCaptionsKeys.all, 'recent-draft'] as const,
  /** "Meus laudos" — lista completa (todos os status) pra tela /laudo. */
  history: () => [...photoCaptionsKeys.all, 'history'] as const,
}

export const claimDetailKeys = {
  all: ['claim-detail'] as const,
  detail: (id: string) => [...claimDetailKeys.all, 'detail', id] as const,
  history: (id: string) => [...claimDetailKeys.all, 'history', id] as const,
  notes: (id: string, params: { page?: number; limit?: number; q?: string } = {}) =>
    [...claimDetailKeys.all, 'notes', id, params] as const,
  attachments: (id: string, params: { source?: string } = {}) =>
    [...claimDetailKeys.all, 'attachments', id, params] as const,
  /** Nº de folhas e tamanho de cada uma de um anexo (modal de preview, `client/claims/attachment-images`). */
  attachmentPages: (id: string, attachmentId: string) =>
    [...claimDetailKeys.all, 'attachment-pages', id, attachmentId] as const,
  eligibleUsers: (id: string, stage: string, inspectionType?: string | null) =>
    [...claimDetailKeys.all, 'eligible-users', id, stage, inspectionType ?? null] as const,
  usersByPermission: (id: string, permission: string) =>
    [...claimDetailKeys.all, 'users-by-permission', id, permission] as const,
  allUsers: (id: string) =>
    [...claimDetailKeys.all, 'all-users', id] as const,
  inspectionStatus: (id: string) =>
    [...claimDetailKeys.all, 'inspection-status', id] as const,
  /**
   * Roots a serem invalidados quando uma mutation do detalhe altera dados
   * que aparecem em outras superfícies (lista de claims, etc).
   *
   * Consumer canonical:
   * ```ts
   * for (const root of claimDetailKeys.affectedByClaimChange()) {
   *   await qc.invalidateQueries({ queryKey: root })
   * }
   * ```
   */
  affectedByClaimChange: () =>
    [claimDetailKeys.all, claimsKeys.all] as const,
}

export const payrollKeys = {
  all: ['payroll'] as const,
  payslips: () => [...payrollKeys.all, 'payslips'] as const,
  users: (query: string) => [...payrollKeys.all, 'users', query] as const,
  documentTypes: () => [...payrollKeys.all, 'document-types'] as const,
}

/** Harness de IA: `status` = ligado para a sessão (flag + schema). */
export const aiKeys = {
  all: ['ai'] as const,
  status: () => [...aiKeys.all, 'status'] as const,
  /** Medições da IA (§15, só ADMIN). */
  claimMetrics: (claimId: string) => [...aiKeys.all, 'claim-metrics', claimId] as const,
  /** "Custo da IA": o período e cada sinistro. */
  costs: (period: string) => [...aiKeys.all, 'costs', period] as const,
  claimCost: (claimId: string) => [...aiKeys.all, 'claim-cost', claimId] as const,
  performance: (params: { period: string; task: string | null }) => [...aiKeys.all, 'performance', params] as const,
  execution: (executionId: string) => [...aiKeys.all, 'execution', executionId] as const,
  /** Documentos e visualizador (invalidados pelo cutucão `ai:claim:<id>`). */
  claimDossier: (claimId: string) => [...aiKeys.all, 'claim-dossier', claimId] as const,
  claimDocument: (claimId: string, documentId: string) => [...aiKeys.all, 'claim-document', claimId, documentId] as const,
  claimDocumentPage: (claimId: string, documentId: string, pageNo: number) => [...aiKeys.all, 'claim-document-page', claimId, documentId, pageNo] as const,
  /** Painel "IA do sinistro": documentos, alertas e a conversa. */
  claimPanel: (claimId: string) => [...aiKeys.all, 'claim-panel', claimId] as const,
  /** Os dois relatórios do sinistro (prefixo: o cutucão `report` relê tudo, inclusive a faixa). */
  claimReportAll: (claimId: string) => [...aiKeys.all, 'claim-report', claimId] as const,
  claimReport: (claimId: string, kind: 'preliminar' | 'final') => [...aiKeys.all, 'claim-report', claimId, kind] as const,
  /** Bloco "Aviso de sinistro" da página do sinistro. Dentro de claim-report: o cutucão `report` relê o estado do trecho do parecer. */
  claimNotice: (claimId: string) => [...aiKeys.all, 'claim-report', claimId, 'notice'] as const,
  /** Dentro de `claimReport`: o cutucão `report` (que invalida o prefixo) relê a faixa do sinistro também. */
  /** Créditos da busca de preços (Serper). */
  quoteAccount: () => [...aiKeys.all, 'quote-account'] as const,
  claimReportStatus: (claimId: string) => [...aiKeys.all, 'claim-report', claimId, 'status'] as const,
  /** Dúvidas do Reg do sinistro (chat e relatório). Dentro de claim-report: o cutucão `report` relê junto. */
  claimQuestions: (claimId: string) => [...aiKeys.all, 'claim-report', claimId, 'questions'] as const,
  /** Histórico de edição do cadastro (sinistro com IA). Dentro de claim-report: o cutucão `report` relê junto. */
  claimEditHistory: (claimId: string) => [...aiKeys.all, 'claim-report', claimId, 'edit-history'] as const,
  /** Conversa da pessoa com a IA no sinistro. */
  agentConversation: (claimId: string) => [...aiKeys.all, 'agent-conversation', claimId] as const,
  /** Cartão da regra da equipe proposta no chat. */
  ruleProposal: (claimId: string, proposalId: string) => [...aiKeys.all, 'rule-proposal', claimId, proposalId] as const,
  /** Tipos de documento: o cadastro (tela de admin) e o que o anexo do sinistro usa (obrigatório? + tipos ativos). */
  documentTypes: (includeInactive: boolean) => [...aiKeys.all, 'document-types', includeInactive] as const,
  /** Modelos dos relatórios preliminar e final (tela Modelos do relatório). */
  reportTemplates: () => [...aiKeys.all, 'report-templates'] as const,
  claimDocumentTypes: (claimId: string) => [...aiKeys.all, 'document-types', 'claim', claimId] as const,
  /** Biblioteca do Reg (tela /admin/biblioteca): a lista e, por versão, o detalhe, a árvore, o trecho e a busca. */
  kbBiblioteca: () => [...aiKeys.all, 'kb'] as const,
  kbBase: (slug: string) => [...aiKeys.all, 'kb', 'base', slug] as const,
  /** Aba Skills: as ensinadas pela equipe (dentro de `kbBiblioteca`). */
  kbSkills: () => [...aiKeys.all, 'kb', 'skills'] as const,
  kbResumoOcorrencia: () => [...aiKeys.all, 'kb', 'resumo-ocorrencia'] as const,
  /** Aba Memória: as notas do dreamer (memória do Reg, 20/10). */
  kbMemoria: () => [...aiKeys.all, 'kb', 'memoria'] as const,
  kbVersion: (id: string) => [...aiKeys.all, 'kb', 'version', id] as const,
  kbTree: (id: string, ref: string | null) => [...aiKeys.all, 'kb', 'version', id, 'tree', ref ?? ''] as const,
  kbTrecho: (id: string, ref: string, parte: number) => [...aiKeys.all, 'kb', 'version', id, 'trecho', ref, parte] as const,
  kbSearch: (id: string, q: string, dentro: string | null) => [...aiKeys.all, 'kb', 'version', id, 'search', q, dentro ?? ''] as const,
  /** Carta de recusa (13/10): o diálogo "Emitir recusa" (prefixo) e cada carta. */
  claimLetters: (claimId: string) => [...aiKeys.all, 'claim-letters', claimId] as const,
  claimLetter: (claimId: string, letterId: string) => [...aiKeys.all, 'claim-letters', claimId, letterId] as const,
}

/** Pedidos (bug, melhoria, funcionalidade). Enviar um pedido invalida as listas. */
export const workRequestsKeys = {
  all: ['work-requests'] as const,
  lists: () => [...workRequestsKeys.all, 'list'] as const,
  list: (params: { type: string | null; page: number }) => [...workRequestsKeys.lists(), params] as const,
  details: () => [...workRequestsKeys.all, 'detail'] as const,
  detail: (id: string) => [...workRequestsKeys.details(), id] as const,
}
